import * as React from "react";
import { navTo } from "@origin-digital/event-dispatcher";
import { useFeatureFlag } from "@origin-digital/feature-flags";
import {
  Stack,
  Heading,
  Button,
  Text,
  MarkdownLite,
  Columns,
  Column,
  TextLink,
} from "@origin-digital/ods-core";
import { useAppContext } from "../../AppContext";
import content from "../../cms/chat.json";
import { AsyncMessenger } from "./AsyncMessenger";

interface IChatProps {
  isNative: boolean;
  nativeChatSupported: boolean | undefined;
}

interface ILiveChatSectionProps {
  authenticated: boolean;
  loading: boolean;
}

// Desktop
const LiveChatSection = ({
  authenticated,
  loading,
}: ILiveChatSectionProps): JSX.Element => (
  <Stack alignX={["left", "center"]}>
    <Button
      data-id="contact-us-button"
      status={loading ? "loading" : undefined}
      onClick={() => (authenticated ? navTo.messageUs() : navTo.contactUs())}
    >
      {content.contactUs.button}
    </Button>
  </Stack>
);

// Desktop — flag off
const ContactUsSection = (): JSX.Element => {
  const { contactUs } = content;
  return (
    <>
      <Stack alignX={["left", "center"]}>
        <Button data-id="contact-us-button" onClick={() => navTo.contactUs()}>
          {contactUs.button}
        </Button>
      </Stack>
    </>
  );
};

// Native — flag on
// Do not destructure `content.contactUsOrEmail` properties will be undefined. This might be a wp5 bug.
// For some reason, if `content.contactUsOrEmail` used WP minification rename props of content.contactUsOrEmail.
const MessageUsSection = (): JSX.Element => (
  <Columns
    alignX={["left", "center"]}
    collapseBelow="lg"
    data-id="chat-item-other"
  >
    <Column width="2/3">
      <Stack alignX={["left", "center"]}>
        <Text align={["left", "center"]} data-id="contact">
          <TextLink onClick={() => navTo.messageUs()} href="#">
            {content.contactUsOrEmail.contactUs}
          </TextLink>
          <MarkdownLite>{`${content.contactUsOrEmail.email}`}</MarkdownLite>
        </Text>
      </Stack>
    </Column>
  </Columns>
);

// Native — flag off
const PhoneAndEmailSection = (): JSX.Element => {
  // Do not destructure `content.phoneOrEmail` properties will be undefined. This might be a wp5 bug.
  // For some reason, if `content.phoneOrEmail` used WP minification rename props of content.phoneOrEmail.
  const { phoneOrEmail } = content;
  return (
    <Columns
      alignX={["left", "center"]}
      collapseBelow="lg"
      data-id="chat-item-other"
    >
      <Column width="2/3">
        <Stack alignX={["left", "center"]}>
          <Text align={["left", "center"]} data-id="contact">
            <MarkdownLite>{`${phoneOrEmail.phone}${phoneOrEmail.email}`}</MarkdownLite>
          </Text>
        </Stack>
      </Column>
    </Columns>
  );
};

export const Chat = (chatProps: IChatProps): JSX.Element => {
  const { nativeChatSupported, isNative } = chatProps;
  const { heading } = content;
  const chatForContactUs = useFeatureFlag("chatForContactUs");
  const { isAuthenticated, categories } = useAppContext();

  return (
    <>
      {nativeChatSupported && <AsyncMessenger />}
      <Columns
        alignX={["left", "center"]}
        collapseBelow="lg"
        space={["none", "xxxlarge"]}
        data-id="chat"
      >
        <Column width="2/3">
          <Stack alignX={["left", "center"]} space={["medium", "medium"]}>
            <Heading variant="h3">{heading}</Heading>
            <Stack space={["xlarge", "xxlarge"]}>
              {chatForContactUs ? (
                isNative ? (
                  <MessageUsSection />
                ) : (
                  <LiveChatSection
                    authenticated={isAuthenticated}
                    loading={categories?.loading}
                  />
                )
              ) : isNative ? (
                <PhoneAndEmailSection />
              ) : (
                <ContactUsSection />
              )}
            </Stack>
          </Stack>
        </Column>
      </Columns>
    </>
  );
};
